import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import "bootstrap/dist/css/bootstrap.min.css";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import moment from "moment";
import {
  getWithAuthCallWithErrorResponse,
  PostCallWithErrorResponse,
} from "../api/ApiServices";
import ApiConfig from "../api/ApiConfig";
import { ToastMsg } from "./ToastMsg";
import Loader from "./Loader";

// Kept in sync with the backend VerificationStatus enum. These are the states an
// admin can move a request into.
const STATUSES = [
  "SUBMITTED",
  "UNDER_REVIEW",
  "NEED_MORE_INFO",
  "AWAITING_PAYMENT",
  "IN_PROGRESS",
  "COMPLETED",
  "APPROVED",
  "REJECTED",
];

// Display labels only — the enum values are what the backend and mobile app
// branch on, so they must not change.
const LABEL = {
  AWAITING_SUBJECT_CONSENT: "Pending Approval",
  SUBMITTED: "Submitted",
  UNDER_REVIEW: "Under review",
  NEED_MORE_INFO: "Need more info",
  AWAITING_PAYMENT: "Awaiting payment",
  IN_PROGRESS: "In progress",
  COMPLETED: "Completed",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  DECLINED_BY_SUBJECT: "Declined by member",
};

const badgeClass = (s) => {
  if (["APPROVED", "COMPLETED"].includes(s)) return "bg-success";
  if (["REJECTED", "DECLINED_BY_SUBJECT", "CANCELLED"].includes(s)) return "bg-danger";
  if (s === "AWAITING_SUBJECT_CONSENT") return "bg-warning text-dark";
  return "bg-secondary";
};

const BackgroundVerification = () => {
  const { sidebar } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [detail, setDetail] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const url = statusFilter
      ? `${ApiConfig.VERIFICATION_LIST}?status=${statusFilter}`
      : ApiConfig.VERIFICATION_LIST;
    getWithAuthCallWithErrorResponse(url)
      .then((res) => setRows(res?.json?.data ?? []))
      .catch(() => ToastMsg("error", "Could not load verification requests"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);

  const openDetail = (id) => {
    getWithAuthCallWithErrorResponse(`${ApiConfig.VERIFICATION_DETAIL}/${id}`)
      .then((res) => {
        if (res?.json?.data) {
          setDetail(res.json.data);
          setNewStatus(res.json.data.status);
          setNote("");
        }
      })
      .catch(() => ToastMsg("error", "Could not load the request"));
  };

  const submitStatus = () => {
    if (!detail) return;
    setSaving(true);
    PostCallWithErrorResponse(
      `${ApiConfig.VERIFICATION_STATUS}/${detail._id}/status`,
      JSON.stringify({ status: newStatus, note })
    )
      .then((res) => {
        if (res?.json?.error) {
          ToastMsg("error", res.json.message || "Update failed");
        } else {
          ToastMsg("success", "Status updated and the member was notified");
          setDetail(null);
          load();
        }
      })
      .catch(() => ToastMsg("error", "Update failed"))
      .finally(() => setSaving(false));
  };

  const memberCell = (m) =>
    m ? (
      <div className="d-flex align-items-center">
        <img
          src={m.profilePicture || "/default-avatar.png"}
          alt=""
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            objectFit: "cover",
            marginRight: 8,
          }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>{m.fullName || "—"}</div>
          <div className="text-muted small">{m.profileId || ""}</div>
        </div>
      </div>
    ) : (
      "—"
    );

  return (
    <main
      id="cx-main"
      className={sidebar ? "admin-main" : "cx-active admin-main"}
    >
      <div className="heading-btn-main">
        <div className="heading-common">
          <label htmlFor="">Background Verification</label>
        </div>
      </div>

      <p className="text-muted small">
        Optional Background Verification requests. Nothing proceeds until the
        member being verified has consented. Changing a status notifies the
        requester (marryem chat + push).
      </p>

      <div className="documents-wrapper">
        <div className="search-wrapper row align-items-end">
          <Form.Group className="col-md-3">
            <Form.Label className="label-select">Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {Object.keys(LABEL).map((s) => (
                <option key={s} value={s}>
                  {LABEL[s]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="col-md-3">
            <button className="btn btn-outline-secondary" onClick={load}>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <Table responsive className="mt-3">
            <thead>
              <tr>
                <th>Verification Requested By</th>
                <th>Person Being Verified</th>
                <th>Verification Level</th>
                <th>Member Consent</th>
                <th>Current Status</th>
                <th>Country</th>
                <th>Request Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No verification requests.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id}>
                    <td>{memberCell(r.requester)}</td>
                    <td>{memberCell(r.subject)}</td>
                    <td>{r.level}</td>
                    <td>
                      <span
                        className={`badge ${
                          r.subjectConsent?.status === "GRANTED"
                            ? "bg-success"
                            : r.subjectConsent?.status === "DECLINED"
                            ? "bg-danger"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {r.subjectConsent?.status || "PENDING"}
                      </span>
                    </td>
                    <td>
                      {/* Admin wording wins over the API's `statusLabel`: that
                          one also feeds the member-facing push notifications,
                          where the longer explanation reads better. */}
                      <span className={`badge ${badgeClass(r.status)}`}>
                        {LABEL[r.status] || r.statusLabel || r.status}
                      </span>
                    </td>
                    <td className="small">
                      {r.requesterCountry || r.subjectCountry || "—"}
                    </td>
                    <td className="small">
                      {r.createdAt
                        ? moment(r.createdAt).format("DD MMM YYYY")
                        : "—"}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openDetail(r._id)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* ---- Detail + status control ---- */}
      <Modal
        show={Boolean(detail)}
        onHide={() => setDetail(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Verification request</Modal.Title>
        </Modal.Header>
        {detail && (
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 mb-3">
                <h6>Verification Requested By</h6>
                {memberCell(detail.requester)}
                <div className="small text-muted mt-1">
                  {detail.requester?.country}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <h6>Person Being Verified</h6>
                {memberCell(detail.subject)}
                <div className="small text-muted mt-1">
                  {detail.subject?.country}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-2">
                <strong>Verification Level:</strong> {detail.level}
              </div>
              <div className="col-md-4 mb-2">
                <strong>Relationship Status:</strong> {detail.matchStage || "—"}
              </div>
              <div className="col-md-4 mb-2">
                <strong>Member Consent:</strong>{" "}
                {detail.subjectConsent?.status || "PENDING"}
              </div>
            </div>

            <div className="alert alert-light border small">
              <strong>Requester acknowledged:</strong>{" "}
              policy {detail.requesterConsent?.policyAccepted ? "✓" : "✗"}, fee{" "}
              {detail.requesterConsent?.feeAcknowledged ? "✓" : "✗"}
              {detail.requesterConsent?.acceptedAt
                ? ` on ${moment(detail.requesterConsent.acceptedAt).format(
                    "DD MMM YYYY, HH:mm"
                  )}`
                : ""}
            </div>

            {/* Message thread */}
            {detail.messages?.length > 0 && (
              <div className="mb-3">
                <h6>Messages</h6>
                <div
                  style={{ maxHeight: 160, overflowY: "auto" }}
                  className="border rounded p-2"
                >
                  {detail.messages.map((m, i) => (
                    <div key={i} className="mb-2 small">
                      <span
                        className={`badge ${
                          m.fromAdmin ? "bg-primary" : "bg-secondary"
                        } me-2`}
                      >
                        {m.fromAdmin ? "Admin" : "Member"}
                      </span>
                      {m.body}
                      <div className="text-muted" style={{ fontSize: 10 }}>
                        {moment(m.at).format("DD MMM YYYY, HH:mm")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.status === "AWAITING_SUBJECT_CONSENT" && (
              <div className="alert alert-warning small">
                This request is still awaiting the subject's consent. Please wait
                for them to accept before progressing it.
              </div>
            )}

            <div className="row">
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Set Current Status</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {LABEL[s]}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-12 mb-3">
                <Form.Label>
                  Message to the member (optional — sent with the status change)
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Verification is available in your country. The fee is … — reply here to proceed."
                />
              </Form.Group>
            </div>
          </Modal.Body>
        )}
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setDetail(null)}
          >
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={submitStatus}
            disabled={saving}
          >
            {saving ? "Saving…" : "Update & notify member"}
          </button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default BackgroundVerification;
